import { useCallback, useMemo, useState } from 'react';
import moment from 'moment';
import { Box, Flex } from '@chakra-ui/react';

import { Table, TableFilter } from '@app/components/common/table';
import { useAlert, useApi } from '@app/hooks';
import { ScanningItem, ScanningTotal, EquipmentScanningReport, EquipmentScanningItem } from '@app/api/models';
import { API_FEAT_DATE_FORMAT } from '@app/utils';
import { DateRangeField, Paper, Txt } from '@app/components/common';
import { DateRange } from '@app/components/common/date-range-picker/types';
import Dialog from '@app/components/common/modals/dialog';
import { Level } from '@app/api/models/structure';
import { TableBase } from '@app/components/common/table/table-base';

import { useColumns } from './columns';
import { scanningColumns } from './scanning-columns';
import { EquipmentScanningFilter, ScanningNodeItem } from './types';
import { getArrayFilterValue, getFilterValue } from '../utils/filter';

export const EquipmentsScanningTable = () => {
  const api = useApi();
  const { success } = useAlert();
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState<DateRange>({
    startDate: moment().subtract(1, 'day'),
    endDate: moment()
  });
  const [data, setData] = useState<EquipmentScanningReport>({
    total: {
      scanning: 0,
      inventory: 0,
    },
    report_data: {},
  });
  const [levels, setLevels] = useState<Level[]>([]);
  const [chain, setChain] = useState<ScanningNodeItem[]>([]);
  const [total, setTotal] = useState<ScanningTotal>({scanning: 0, inventory: 0}); 
  const [filter, setFilter] = useState<TableFilter<ScanningNodeItem>[]>([
    {
      name: 'user', label: 'Имя пользователя', type: 'text', value: '',
      source: '',
      scope: 'include'
    },
    {
      name: 'full_name', label: 'Пользователь', type: 'text', value: '',
      source: '',
      scope: 'include'
    },
    {
      name: 'serial_num', label: 'Серийный номер', type: 'text', value: '',
      source: '',
      scope: 'include'
    },
    {
      name: 'inventory_num', label: 'Инв.номер', type: 'text', value: '',
      source: '',
      scope: 'include'
    },
    {
      name: 'model', label: 'Модель', type: 'multi-select', value: [],
      source: 'equipment_model',
      scope: 'include'
    },
    {
      name: 'brand', label: 'Бренд', type: 'multi-select', value: [],
      source: 'equipment_brand',  
      scope: 'include'
    },
    {
      name: 'manufacturer', label: 'Производитель', type: 'multi-select', value: [],
      source: 'equipment_manufacturer',
      scope: 'include'
    },
  ]);

  const [showDetail, setShowDetail] = useState<ScanningNodeItem>();

  const fetchData = useCallback(async ({ period, filter }: {
    period: DateRange,
    filter: EquipmentScanningFilter
  }) => { 
    setLoading(true);
    const { data: levelsData } = await api.structure.getLevels({
      filter: {
        is_active: 'true',
      },
      sort: '-value',
      limit: 10,
      offset: 0,
    });

    setLevels([...levelsData.data, 
      {
        id: -1,
        name: 'Outlets',
        value: -1,
        is_active: true,
      }
    ]);
    setChain([]);

    const { data } = await api.reports.getEquipmentsScanning({ 
      filter: {
        date_b: period.startDate.format(API_FEAT_DATE_FORMAT),
        date_e: period.endDate.format(API_FEAT_DATE_FORMAT),
        ...filter,
      },
      download: false
    });
    setData(data);
    setTotal(data?.total || { scanning: 0, inventory: 0 });
    setLoading(false);
  }, []);

  const tableData = useMemo(() => {
    if (!data) {return [];}
    if (chain.length === 0) {
      return [...Object.keys(data.report_data).map<ScanningNodeItem>((key) => {
        return {
          id: key,
          title: data.report_data[key].employee?.name || 'Вакансия',
          subtitle: data.report_data[key].territory,
          level: data.report_data[key].level,
          total: data.report_data[key].total,
        };
      }),
        {
          id: '-1',
          title: 'Итого',
          subtitle: '',
          level: levels[0],
          total: data.total,
        }
      ];
    }

    let currentNode: EquipmentScanningItem = data.report_data[chain[0].id];

    for (const item of chain.slice(1)) {
      currentNode = currentNode.subordinates[item.id];
    }
    
    return [
      ...chain, 
      ...Object.keys(currentNode.subordinates ||  {}).map<ScanningNodeItem>((key) => ({
        id: key,
        title: key == currentNode.subordinates[key].level.name ? '' : currentNode.subordinates[key].employee?.name || 'Вакансия',
        subtitle: key == currentNode.subordinates[key].level.name ? '[пусто]' :  currentNode.subordinates[key].territory,
        level: currentNode.subordinates[key].level,
        total: currentNode.subordinates[key].total,
      })),
      ...Object.keys(currentNode.outlet || {}).map<ScanningNodeItem>((key) => {
        return {
          id: key,
          title: `[${key}] ` +  (currentNode.outlet[key]?.outlet?.name || 'ТТ неизвестно'),
          subtitle: currentNode.outlet[key]?.full_address || '-',
          level: levels[levels.length - 1],
          total: {
            inventory: currentNode.outlet[key]?.total.inventory || 0,
            scanning: currentNode.outlet[key]?.total.scanning || 0,
          },
        };
      }),
      {
        id: '-1',
        title: 'Итого',
        subtitle: '',
        level: levels[0],
        total: data.total,
      }
    ]; 
  }, [data, chain, levels]);

  const onFilterChange = useCallback((filter: TableFilter<ScanningNodeItem>[]) => {
    setFilter(filter);
    fetchData({ period, filter: {
      user: getFilterValue<ScanningNodeItem>(filter, 'user'),
      full_name: getFilterValue<ScanningNodeItem>(filter, 'full_name'),
      serial_num: getFilterValue<ScanningNodeItem>(filter, 'serial_num'),
      inventory_num: getFilterValue<ScanningNodeItem>(filter, 'inventory_num'),
      model_id: getArrayFilterValue<ScanningNodeItem>(filter, 'model'),
      brand_id: getArrayFilterValue<ScanningNodeItem>(filter, 'brand'),
      manufacturer_id: getArrayFilterValue<ScanningNodeItem>(filter, 'manufacturer'),
    }
  });
  }, [fetchData, period]);

  const onItemClick = useCallback((item: ScanningNodeItem) => {
    setChain([...chain, item]);
  }, [chain]);

  const onMoveUpClick = useCallback((item: ScanningNodeItem) => {
    const index = chain.findIndex(({ id }) => id === item.id);
    setChain(chain.slice(0, index));
  }, [chain]);

  const onShowDetail = useCallback((item: ScanningNodeItem) => {
   setShowDetail(item);
  }, []);

  const columns = useColumns({ chain, levels, onItemClick, onMoveUpClick, onShowDetail });


  const onChangePeriod = useCallback((period: DateRange) => {
    setPeriod(period);
    fetchData({ period, filter: {
        user: getFilterValue(filter, 'user'),
        full_name: getFilterValue(filter, 'full_name'),
        serial_num: getFilterValue(filter, 'serial_num'),
        inventory_num: getFilterValue(filter, 'inventory_num'),
        model_id: getArrayFilterValue(filter, 'model'),
        brand_id: getArrayFilterValue(filter, 'brand'),
        manufacturer_id: getArrayFilterValue(filter, 'manufacturer'),
      }
    });
  }, [fetchData, filter]);

  const download = useCallback(async () => {
    setLoading(true);
    const { code, message } = await api.reports.getEquipmentsScanning({ 
      filter: {
        date_b: period.startDate.format(API_FEAT_DATE_FORMAT),
        date_e: period.endDate.format(API_FEAT_DATE_FORMAT),
        user: getFilterValue(filter, 'user'),
        full_name: getFilterValue(filter, 'full_name'),
        serial_num: getFilterValue(filter, 'serial_num'),
        inventory_num: getFilterValue(filter, 'inventory_num'),
        model_id: getArrayFilterValue(filter, 'model'),
        brand_id: getArrayFilterValue(filter, 'brand'),
        manufacturer_id: getArrayFilterValue(filter, 'manufacturer'),
      },
      download: true
    });
    if (code === 200) {
      success(message);
    }
    setLoading(false);
  }, [period, filter]);

  const scanningData = useMemo(() => {
    if (!showDetail) {return [];}

    const addOutlet = (outlet: EntityBase, scans: ScanningItem[]) => {
      return scans.map((item) => ({ ...item, equip: { ...item.equip, outlet } }));
    };

    const recur = (item?: EquipmentScanningItem) => {
      if (item?.subordinates) {return Object.values(item.subordinates).map(recur).flat();}
      if (item?.outlet) {
        return Object.values(item.outlet).map(({ scans, outlet }) => addOutlet(outlet, scans)).flat();
      }
      return [];
    };

    if (chain.length === 0 || showDetail.id === chain[0].id) { return recur(data.report_data[showDetail.id]); }

    let currentNode: EquipmentScanningItem = data.report_data[chain[0].id];

    for (const item of chain.slice(1)) {
      if (item.level.id === -1 || item.id === showDetail.id) { break; }
      currentNode = currentNode.subordinates[item.id];
    }

    if (showDetail.level.id === -1) {
      return addOutlet(currentNode.outlet[showDetail.id].outlet, currentNode.outlet[showDetail.id].scans);
    }


    currentNode = currentNode.subordinates[showDetail.id];


    return recur(currentNode);
  }, [data, chain, showDetail]);

  return <>
    <Box mb={2}>
      <Paper>
        <Box px={6} py={4} display="flex">
          <Box mr={2} minWidth={300}>
            <DateRangeField
              id='period'
              label="Период"
              value={period}
              onChange={onChangePeriod}
            />
          </Box>
        </Box>
      </Paper>
    </Box>
    <TableBase<ScanningNodeItem> 
      title="Отчет по сканированию ХО, PM"  
      columns={columns}
      data={tableData}
      loading={loading}
      onDownload={download}
      filter={filter}
      onFilterChange={onFilterChange}
    />
    <Dialog open={!!showDetail}
      onClose={() => setShowDetail(undefined)}
      title="Отчет по сканированию ХО, PM" 
      width={800}
    >
      <Box margin={-4}>
        <Table<ScanningItem>
          title=""
          columns={scanningColumns}
          data={scanningData}
          paper={false}
        />
      </Box>
    </Dialog>
  </>;
};